import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { previewImportPurchases, importPurchases, downloadPurchasesTemplate } from '../../api/client'

// Import requests from Excel: get the template, fill it in, see what would be created and which rows need fixing,
// then create them as drafts. One row is one line; rows sharing a "Solicitud" make one multi-line request.
function Cifra({ label, v, tone }) {
  return (
    <div className="rounded-lg border px-3 py-2 text-center">
      <div className={`text-xl font-semibold ${tone || 'text-gray-800'}`}>{v}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}

export default function ImportarSolicitudes({ onClose, onDone }) {
  const { t } = useTranslation(['purchases', 'common'])
  const [fase, setFase] = useState('elegir')        // elegir | leyendo | previa | cargando | listo
  const [file, setFile] = useState(null)
  const [previa, setPrevia] = useState(null)
  const [resultado, setResultado] = useState(null)
  const [error, setError] = useState('')
  const input = useRef(null)
  const ocupado = fase === 'leyendo' || fase === 'cargando'

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape' && !ocupado) onClose() }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose, ocupado])

  const msg = (e) => {
    const d = e.response?.data?.detail
    return typeof d === 'string' ? d : (d?.message || t('importRequests.failed'))
  }
  const elegir = async (f) => {
    if (!f) return
    setFile(f); setError(''); setFase('leyendo')
    try { setPrevia(await previewImportPurchases(f)); setFase('previa') } catch (e) { setError(msg(e)); setFase('elegir') }
  }
  const cargar = async () => {
    setFase('cargando'); setError('')
    try { setResultado(await importPurchases(file)); setFase('listo'); onDone() } catch (e) { setError(msg(e)); setFase('previa') }
  }
  const plantilla = async () => { try { await downloadPurchasesTemplate() } catch (e) { setError(msg(e)) } }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={ocupado ? undefined : onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-4" role="dialog" aria-label={t('importRequests.title')}>
        <h2 className="text-lg font-semibold text-gray-900">{t('importRequests.title')}</h2>
        <p className="text-sm text-gray-500">{t('importRequests.intro')}</p>
        {error && <p className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">{error}</p>}

        {(fase === 'elegir' || fase === 'leyendo') && (
          <div className="space-y-3">
            <input ref={input} type="file" accept=".xlsx" className="hidden" onChange={e => { elegir(e.target.files?.[0]); e.target.value = '' }} />
            <div className="flex flex-wrap items-center gap-2">
              <button disabled={fase === 'leyendo'} onClick={() => input.current?.click()}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50">
                {fase === 'leyendo' ? t('importRequests.reading') : t('importRequests.choose')}
              </button>
              <button type="button" onClick={plantilla}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50">↓ {t('importRequests.template')}</button>
              <button type="button" onClick={onClose} className="ml-auto text-sm text-gray-500 hover:text-gray-800">{t('common:actions.cancel')}</button>
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-xs text-gray-600 space-y-1.5">
              <div className="font-medium text-gray-700">{t('importRequests.howTitle')}</div>
              <ul className="list-disc pl-4 space-y-1">
                <li>{t('importRequests.how1')}</li>
                <li>{t('importRequests.how2')}</li>
                <li>{t('importRequests.how3')}</li>
              </ul>
            </div>
          </div>
        )}

        {(fase === 'previa' || fase === 'cargando') && previa && (
          <>
            <div className="grid grid-cols-4 gap-2">
              <Cifra label={t('importRequests.lines')} v={previa.lineas} />
              <Cifra label={t('importRequests.new')} v={previa.nuevas} tone="text-emerald-600" />
              <Cifra label={t('importRequests.review')} v={previa.a_revisar} tone={previa.a_revisar ? 'text-red-600' : undefined} />
              <Cifra label={t('importRequests.existing')} v={previa.existentes} />
            </div>
            <ul className="border rounded-lg max-h-72 overflow-y-auto text-xs divide-y">
              {previa.solicitudes.map((s, i) => (
                <li key={i} className="px-3 py-2">
                  <div className="flex items-start gap-2">
                    <span className={`shrink-0 px-1.5 rounded text-[11px] font-medium ${s.estado === 'nueva' ? 'bg-emerald-50 text-emerald-700' : s.estado === 'existe' ? 'bg-gray-100 text-gray-600' : 'bg-red-50 text-red-700'}`}>
                      {t(`importRequests.state.${s.estado}`)}
                    </span>
                    <div className="min-w-0 flex-1">
                      {s.estado === 'existe' ? (
                        <span className="text-gray-600">{t('importRequests.alreadyThere', { n: s.existe })}</span>
                      ) : (
                        <>
                          <div className="font-medium text-gray-800">
                            {[s.proyecto, s.area, s.proveedor].filter(Boolean).join(' · ') || t('importRequests.noData')}
                            {s.lineas && <span className="text-gray-400 font-normal"> · {t('importRequests.nLines', { count: s.lineas.length })}</span>}
                          </div>
                          {s.lineas && <div className="text-gray-500 truncate">{s.lineas.slice(0, 3).map(l => `${l.cantidad}× ${l.descripcion || '—'}`).join(' · ')}{s.lineas.length > 3 ? ' …' : ''}</div>}
                        </>
                      )}
                      {s.problemas.map((p, j) => <div key={j} className="text-red-600">• {p}</div>)}
                      {s.avisos.map((a, j) => <div key={j} className="text-amber-700">• {a}</div>)}
                      <div className="text-gray-400">{t('importRequests.rows', { rows: s.filas.join(', ') })}</div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {previa.a_revisar > 0 && <p className="text-xs text-gray-500">{t('importRequests.reviewNote')}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => { setFase('elegir'); setPrevia(null) }} disabled={fase === 'cargando'} className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100">{t('importRequests.another')}</button>
              <button onClick={cargar} disabled={fase === 'cargando' || previa.nuevas === 0}
                className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40">
                {fase === 'cargando' ? t('importRequests.creating') : t('importRequests.create', { count: previa.nuevas })}
              </button>
            </div>
          </>
        )}

        {fase === 'listo' && resultado && (
          <>
            <p className="px-3 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm">{t('importRequests.done', { count: resultado.creadas.length })}</p>
            {resultado.creadas.length > 0 && (
              <p className="text-sm text-gray-600">
                {resultado.creadas.map((c, i) => <span key={c.id}>{i ? ', ' : ''}<Link to={`/purchases/${c.id}`} className="text-indigo-700 hover:underline">#{c.id}</Link></span>)}
              </p>
            )}
            {resultado.fallidas.length > 0 && (
              <ul className="text-xs text-red-600">{resultado.fallidas.map((f, i) => <li key={i}>• {f.grupo}: {f.error}</li>)}</ul>
            )}
            <div className="flex justify-end"><button onClick={onClose} className="bg-gray-900 text-white px-4 py-2 rounded-lg text-sm">{t('importRequests.close')}</button></div>
          </>
        )}
      </div>
    </div>
  )
}
